import React, { useState, useEffect } from "react";
import {
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  Checkbox,
  FormControl,
  InputLabel,
  DialogContentText,
} from "@mui/material";
import { SyntheticEvent } from "react";
import MDButton from "../../../../components/MDButton";
import IconButton from "@mui/material/IconButton";
import { get, post } from "requests";
import { useSubmissionStore } from "../../../../models/applications/SubmissionStore";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ReportGenerator from "./ReportGenerator";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import ExperianIcon from "../../../../assets/images/logos/experian-logo.png";
import ClearIcon from "../../../../assets/images/logos/clear-logo.png";
import FullPageLoader from "../../../../components/Loader/FullPage";

interface BackgroundChecksProps {
  appId: number;
  isReadOnly: boolean;
}

interface Report {
  Name?: string;
  status?: string;
  type?: string;
  reportId?: number;
  dateCreated?: string;
}

interface SuccessPopupProps {
  open: boolean;
  message: string;
  onClose: () => void;
}

interface BusinessResult {
  busName?: string;
  street?: string;
  city?: string;
  state?: string;
  zip5?: string;
  bin?: string;
}

interface ApiError {
  response?: {
    data?: {
      message?: string;
      error?: string;
    };
  };
  message?: string;
  code?: string;
}

const SuccessPopup: React.FC<SuccessPopupProps> = ({ open, message, onClose }) => {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Success</DialogTitle>
      <DialogContent>
        <Typography>{message}</Typography>
      </DialogContent>
      <DialogActions>
        <MDButton variant="gradient" color="primary" onClick={onClose}>
          Close
        </MDButton>
      </DialogActions>
    </Dialog>
  );
};

const BackgroundChecks: React.FC<BackgroundChecksProps> = ({ appId, isReadOnly }) => {
  const {
    affiliatedBusinesses,
    setAffiliatedBusinessesData,
    merchantBusinessInfo,
    fetchInitialData,
  } = useSubmissionStore();
  const [activeTab, setActiveTab] = useState<string>("clear");
  const [reports, setReports] = useState<Report[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [dialogTab, setDialogTab] = useState<string>("personal");
  const [showFooter, setShowFooter] = useState<boolean>(false);
  const [contacts, setContacts] = useState<any[]>([]);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [generating, setGenerating] = useState<boolean>(false);
  const [open, setOpen] = useState(false);
  const [selectedClearContact, setSelectedClearContact] = useState<any>(null);
  const [selectedExperianContact, setSelectedExperianContact] = useState<any>(null);
  const [clearConsumerSearchResult, setClearConsumerSearchResult] = useState<any[]>([]);
  const [clearBusinessSearchResult, setClearBusinessSearchResult] = useState<any[]>([]);
  const [experianConsumerSearchResult, setExperianConsumerSearchResult] = useState<any[]>([]);
  const [experianBusinessSearchResult, setExperianBusinessSearchResult] = useState<any[]>([]);
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [clearReportList, setClearReportList] = useState([]);
  const [selectedBin, setSelectedBin] = useState<any>(null);
  const [msg, setMsg] = useState<string>(""); // Message to display after report generation
  const [searchedPersonOnce, setSearchedPersonOnce] = useState<boolean>(false);
  const [searchedBusinessOnce, setSearchedBusinessOnce] = useState<boolean>(false);
  const [requestCounter, setRequestCounter] = useState<number>(0);
  const [refresh, setRefresh] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [generatingHTML, setGeneratingHTML] = useState(false);

  useEffect(() => {
    const fetchReports = async () => {
      setIsLoading(true);
      setError(null);

      try {
        if (activeTab === "clear") {
          await fetchClearReports();
        }
        // else if (activeTab === 'clear') {
        //   await fetchClearReports();
        // }
      } catch (err) {
        console.error("Error fetching reports:", err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchReports();
  }, [activeTab, appId]);

  const handleApiError = (error: ApiError): string => {
    console.error("API Error:", error);
    if (error.response && error.response.data) {
      const errorData = error.response.data;
      if (errorData.message) {
        return errorData.message;
      }
      if (errorData.error) {
        return errorData.error;
      }
    }
    if (error.message === "Network Error") {
      return "Network error. Please check your internet connection and try again.";
    }
    if (error.code === "ECONNABORTED") {
      return "Request timeout. Please try again.";
    }
    return error.message || "An error occurred while processing your request.";
  };

  const fetchExperianReports = async () => {
    try {
      const response = await get(`v1/fetchAllExperianReports/${appId}`);
      console.log("Fetched Experian reports:", response);
      setReports(response);
    } catch (err) {
      throw new Error("Failed to fetch Experian reports");
    }
  };

  const fetchClearReports = () => {
    get(`clear/getContacts/${appId}`)
      .then((response) => {
        setContacts(response);
        const ids = response.map((item: { id: number }) => String(item.id));
        post(`clear/getReportList`, {
          merchantId: String(merchantBusinessInfo.merchantId),
          usersId: ids,
        })
          .then((response) => {
            if (response.error) {
              console.log("Fetched clear reports:", response);
              setMsg(response.error);
              setGenerating(false);
            } else {
              setClearReportList(response.result && response.result);
            }
            setRequestCounter((prevCounter) => prevCounter - 1);
          })
          .catch((error) => {
            console.log(error);
            setRequestCounter((prevCounter) => prevCounter - 1);
          });
      })
      .catch((error) => {
        console.log(error);
        setRequestCounter((prevCounter) => prevCounter - 1);
      });
  };

  const handleTabChange = (event: SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
    if (newValue === "clear") {
      setReports([]);
    } else if (newValue === "experian") {
      fetchExperianReports();
    }
  };

  const handleDialogTabChange = (event: SyntheticEvent, newValue: string) => {
    setDialogTab(newValue);
  };

  const openDialog = () => {
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
  };

  const handleSelectChange = (e: any) => {
    if (activeTab === "clear") {
      setSelectedClearContact(e.target.value);
    } else if (activeTab === "experian") {
      setSelectedExperianContact(e.target.value);
    }
  };

  const handleCheckboxChange = (id: any) => {
    setSelectedBin(selectedBin === id ? null : id);
  };

  const getExperianReport = (r: Report) => {
    if (r.type === "Business") {
      get(`v1/fetchBusinessHtml/${r.reportId}`)
        .then((response) => {
          if (response.status === "success") {
            const htmlContent = response.htmlContent;
            if (htmlContent && htmlContent.trim().length > 0) {
              const newWindow = window.open();
              if (newWindow) {
                newWindow.document.write(htmlContent);
                newWindow.document.close();
              } else {
                setMsg("Unable to open a new tab. Please check your popup blocker settings.");
              }
            } else {
              throw new Error("Received empty or invalid HTML content.");
            }
          } else {
            throw new Error(`Failed to fetch HTML report for BIN: ${r.reportId}`);
          }
        })
        .catch((error) => {
          console.error("Error fetching report:", error);
          setMsg(error.message || "An error occurred while fetching the report.");
        });
    } else if (r.type === "Consumer") {
      get(`v1/fetchPersonalHTML/${r.reportId}`)
        .then((response) => {
          console.log("response for consumer", response);
          if (response.status === "success") {
            const personalHTMLReport = response.data;
            if (personalHTMLReport && personalHTMLReport.trim().length > 0) {
              const htmlContent = personalHTMLReport;
              const newWindow = window.open();
              if (newWindow) {
                newWindow.document.write(htmlContent);
                console.log("HTML content", htmlContent);
                newWindow.document.close();
              } else {
                setMsg("Unable to open a new tab. Please check your popup blocker settings.");
              }
              return;
            }
          }
          console.warn("HTML report missing or invalid, falling back to JSON API...");
          return get(`v1/fetchPersonalJSON/${r.reportId}`);
        })
        .then((fallbackResponse) => {
          if (!fallbackResponse) return;
          console.log("response for consumer (JSON fallback)", fallbackResponse);
          if (fallbackResponse.status === "success") {
            const personalReport = fallbackResponse.personalJSON;
            if (personalReport && personalReport.trim().length > 0) {
              const parsedJSON = JSON.parse(personalReport);
              console.log("parsed JSON", parsedJSON);

              const htmlContent = ReportGenerator.generateHTMLFromJSON(parsedJSON);

              const newWindow = window.open();
              if (newWindow) {
                newWindow.document.write(htmlContent);
                console.log("Generated HTML content", htmlContent);
                newWindow.document.close();
              } else {
                setMsg("Unable to open a new tab. Please check your popup blocker settings.");
              }
            } else {
              throw new Error("Received empty or invalid JSON content.");
            }
          } else {
            throw new Error(`Failed to fetch Personal JSON report: ${r.reportId}`);
          }
        })
        .catch((error) => {
          console.error("Error fetching report:", error);
          setMsg(error.message || "An error occurred while fetching the report.");
        });
    }
  };

  const getClearReport = (r: any) => {
    console.log("Report details:", r);
    console.log("Report ID:", r[0]);
    if (r[3] === "Business" || r[3] === "Personal") {
      console.log("Fetching report with ID:", r[0]);
      get(`clear/getHtmlReport/${r[0]}`)
        .then((response) => {
          if (response.error) {
            setMsg(response.error);
            setGenerating(false);
          } else {
            const newWindow = window.open();
            if (newWindow) {
              newWindow.document.write(response.result);
              newWindow.document.close();
            } else {
              console.log("Failed to open a new window.");
            }
          }
        })
        .catch((error) => {
          console.log("Error fetching report:", error);
        });
    }
  };

  const handleGenerateReport = () => {
    if (!selectedBin) {
      setMsg("Please select a consumer or business before generating the report.");
      return;
    }
    setGenerating(true);

    if (activeTab === "clear") {
      if (selectedTab === 0) {
        // Clear Personal Search
        console.log("Clear personal triggered");
        const selectedConsumer = clearConsumerSearchResult.find(
          (result) => result?.groupId === selectedBin
        );
        console.log("selectedConsumer for clear personal search", selectedConsumer);
        if (!selectedConsumer) {
          setMsg("Selected consumer not found.");
          setGenerating(false);
          return;
        }

        const request = {
          clearGroupId: selectedConsumer.groupId,
          userId: selectedConsumer.id,
          dealApplicationId: appId,
        };

        post("clear/personSearch/createReport", request)
          .then((response) => {
            setPopupMessage(response.result);
            setPopupOpen(true);
            closeDialog();
            fetchClearReports();
          })
          .catch((error) => {
            console.error("Error generating report:", error);
            setPopupMessage(error.message || "An error occurred while generating the report.");
            setPopupOpen(true);
          })
          .finally(() => {
            setGenerating(false);
          });
      } else if (selectedTab === 1) {
        // Clear Business Search
        console.log("Clear business triggered");
        const selectedBusiness = clearBusinessSearchResult.find(
          (result) => result?.groupId === selectedBin
        );
        console.log("selectedBusiness for clear business search", selectedBusiness);
        if (!selectedBusiness) {
          setMsg("Selected business not found.");
          setGenerating(false);
          return;
        }
        const request = {
          clearGroupId: selectedBusiness.groupId,
          merchantId: selectedBusiness.id,
          dealApplicationId: appId,
        };
        post("clear/businessSearch/createReport", request)
          .then((response) => {
            setPopupMessage(response.result);
            setPopupOpen(true);
            closeDialog();
            fetchClearReports();
          })
          .catch((error) => {
            console.error("Error generating report:", error);
            setPopupMessage(error.message || "An error occurred while generating the report.");
          })
          .finally(() => {
            setGenerating(false);
          });
      }
    } else if (activeTab === "experian") {
      if (selectedTab === 0) {
        // Experian Personal Search
        const selectedConsumer = experianConsumerSearchResult.find(
          (result) => result.id === selectedBin
        );
        console.log("selected consumer for experian consumer search", selectedConsumer);

        if (!selectedConsumer) {
          setPopupMessage("Selected consumer not found.");
          setPopupOpen(true);
          setGenerating(false);
          return;
        }
        setGeneratingHTML(true);
        get(`v1/fetchPersonalJSON/${selectedConsumer.id}`)
          .then((response) => {
            if (response.status === "success") {
              const personalReport = response.data;

              if (personalReport && personalReport.toString().trim().length > 0) {
                const parsedJSON =
                  typeof personalReport === "string" ? JSON.parse(personalReport) : personalReport;

                // Generate HTML content from JSON
                const htmlContent = ReportGenerator.generateHTMLFromJSON(parsedJSON);
                const request = {
                  userId: Number(selectedConsumer.id),
                  dealApplicationId: appId,
                  htmlContent,
                };

                return post("v1/consumerPremierReport/html", request);
              } else {
                throw new Error("Personal report JSON is empty.");
              }
            } else {
              throw new Error("Failed to fetch personal report JSON.");
            }
          })
          .then((response) => {
            if (response && response.status === "success") {
              setPopupMessage("Report successfully generated.");
              setPopupOpen(true);
              closeDialog();
              fetchExperianReports();
            } else {
              throw new Error("Failed to generate report.");
            }
          })
          .catch((error) => {
            console.error("Error generating report:", error);
            setPopupMessage(error.message || "An error occurred while generating the report.");
            setPopupOpen(true);
          })
          .finally(() => {
            setGenerating(false);
            setGeneratingHTML(false);
          });
      } else if (selectedTab === 1) {
        // Experian Business Search
        const selectedBusiness = experianBusinessSearchResult.find(
          (result) => result.bin === selectedBin
        );
        console.log("selectedBusiness for experian business search", selectedBusiness);
        if (!selectedBusiness) {
          setPopupMessage("Selected business not found.");
          setPopupOpen(true);
          setGenerating(false);
          return;
        }
        const tenantId = localStorage.getItem("tenantId");
        const request = {
          tenantId,
          bin: selectedBusiness.bin,
          businessName: selectedBusiness.name,
          dealApplicationId: appId,
        };
        post("v1/bisPremierReport/html", request)
          .then((response) => {
            if (response.status === "success") {
              setPopupMessage("Report successfully generated.");
              setPopupOpen(true);
              closeDialog();
              fetchExperianReports();
            } else {
              throw new Error("Failed to generate report.");
            }
          })
          .catch((error) => {
            console.error("Error generating report:", error);
            setPopupMessage(error.message || "An error occurred while generating the report.");
            setPopupOpen(true);
          })
          .finally(() => {
            setGenerating(false);
          });
      }
    }
  };

  const handleSearchPersonal = () => {
    const tenantId = Number(localStorage.getItem("tenantId"));
    const selectedContact =
      activeTab === "experian" ? selectedExperianContact : selectedClearContact;
    console.log("selectedContact for personal", selectedContact);

    if (!selectedContact || !selectedContact.id) {
      console.error("Selected contact is undefined or invalid");
      return;
    }
    if (activeTab === "experian") {
      setExperianConsumerSearchResult([]);
      setSelectedBin(null);
    } else {
      setClearConsumerSearchResult([]);
      setSelectedBin(null);
    }

    if (activeTab === "experian") {
      const selectedUserId = selectedContact.id;
      console.log("selectedUserId", selectedUserId);
      if (selectedUserId) {
        setSubmitting(true);
        const request = {
          dealApplicationId: appId,
          tenantId: tenantId,
          userId: selectedUserId,
        };
        console.log("Consumer request is", request);

        post("v1/creditReport", request)
          .then((responseData) => {
            console.log(`Credit response for userId ${selectedUserId}:`, responseData);
            if (responseData.status === "error") {
              const errorMessage = responseData.message || "Failed to retrieve credit report.";
              setPopupMessage(errorMessage);
              setPopupOpen(true);
              setSubmitting(false);
              return;
            }

            if (responseData.error) {
              setPopupMessage(responseData.error);
              setPopupOpen(true);
              setSubmitting(false);
              return;
            }

            if (responseData.status === "success" && responseData.data) {
              try {
                const consumerResults = responseData.data;
                const name = consumerResults.creditProfile[0].consumerIdentity.name[0];
                const firstName = name.firstName || "";
                const surname = name.surname || "";
                const fullName = `${firstName} ${surname}`.trim();

                const addressInfo = consumerResults.creditProfile[0].addressInformation[0];
                const streetParts = [
                  addressInfo.streetPrefix,
                  addressInfo.streetName,
                  addressInfo.streetSuffix,
                ]
                  .filter(Boolean)
                  .join(" ");
                const address = `${streetParts}, ${addressInfo.city || ""}, ${
                  addressInfo.state || ""
                }, ${addressInfo.zipCode || ""}`;

                setExperianConsumerSearchResult([
                  {
                    id: request.userId,
                    name: fullName || "Name not available",
                    address: address,
                  },
                ]);
                setShowFooter(true);
                setSelectedTab(0);
                console.log("Processed Experian consumer results:", fullName, address);
              } catch (parseError) {
                console.error("Error parsing credit report data:", parseError);
                setPopupMessage("Invalid credit report data received. Please try again.");
                setPopupOpen(true);
              }
            } else {
              setPopupMessage("Unexpected response format from credit report API.");
              setPopupOpen(true);
            }
            setSubmitting(false);
          })
          .catch((error) => {
            console.error(`Error fetching credit report for userId ${selectedUserId}:`, error);
            const errorMessage = handleApiError(error);
            setPopupMessage(errorMessage);
            setPopupOpen(true);
            setSubmitting(false);
          });
      } else {
        console.error("Selected userId is null or invalid.");
      }
    } else {
      if (selectedContact.id === null || selectedContact.id === "") {
        return;
      }
      setSubmitting(true);
      setSearchedPersonOnce(true);
      setRequestCounter((prevCounter) => prevCounter + 1);
      get(`clear/personSearch/search/${selectedContact.id}`)
        .then((response) => {
          setRefresh(!refresh);
          setClearConsumerSearchResult(
            response.result.map((result: any) => ({
              id: result.id,
              name: result.name,
              address: result.address,
              groupId: result.groupId,
            }))
          );
          setRequestCounter((prevCounter) => prevCounter - 1);
          setSubmitting(false);
          setShowFooter(true);
          // setSelectedTab(0);
        })
        .catch((error) => {
          console.error("Error fetching Clear report:", error);
          const errorMessage = handleApiError(error);
          setPopupMessage(errorMessage);
          setPopupOpen(true);
          setSubmitting(false);
          setRequestCounter((prevCounter) => prevCounter - 1);
        });
    }
  };

  const handleSearchBusiness = () => {
    const tenantId = Number(localStorage.getItem("tenantId"));
    const dealApplicationId = Number(appId);
    const selectedContact =
      activeTab === "experian" ? selectedExperianContact : selectedClearContact;
    console.log("selectedContact for business", selectedContact);
    if (!selectedContact) {
      console.error("Selected contact is undefined or invalid");
      return;
    }
    if (activeTab === "experian") {
      setExperianBusinessSearchResult([]);
      setSelectedBin(null);
    } else {
      setClearBusinessSearchResult([]);
      setSelectedBin(null);
    }

    if (activeTab === "experian") {
      const request: any = {
        tenantId,
        id: selectedContact.merchantId || selectedContact.id,
        name: selectedContact.merchantId
          ? selectedContact.merchantName
          : selectedContact.businessLegalName,
        entityType: selectedContact.merchantId ? "merchant" : "affiliated_business",
        dealApplicationId,
      };
      post("v1/quickSearch", request)
        .then((responseData) => {
          console.log("response", responseData);
          if (responseData.error) {
            setPopupMessage(responseData.error);
            setPopupOpen(true);
            closeDialog();
            setSubmitting(false);
          } else {
            const results: BusinessResult[] = responseData.data.results;
            if (typeof results === "string") {
              setPopupMessage(results);
              setPopupOpen(true);
            } else {
              const businessResults = results.map((result: BusinessResult) => {
                return {
                  name: result.busName,
                  address: `${result.street}, ${result.city}, ${result.state}, ${result.zip5}`,
                  bin: result.bin,
                };
              });
              if (businessResults.length > 0) {
                setExperianBusinessSearchResult(businessResults);
                setSelectedTab(1);
                setShowFooter(true);
              }
              setGenerating(false);
            }
          }
        })
        .catch((error) => {
          console.error("Error during quick search:", error);
          setSubmitting(false);
          setGenerating(false);
        });
    } else {
      if (selectedContact.id === null || selectedContact.id === "") {
        return;
      }
      setSubmitting(true);
      setSearchedBusinessOnce(true);
      setRequestCounter((prevCounter) => prevCounter + 1);
      const isMerchant = !!selectedContact.merchantId;
      const idToUse = isMerchant ? selectedContact.merchantId : selectedContact.id;
      const url = `clear/businessSearch/search/${idToUse}?isMerchant=${isMerchant}`;
      get(url)
        .then((response) => {
          setRefresh(!refresh);
          if (response.error) {
            closeDialog();
            setMsg(response.error);
            setGenerating(false);
          }
          const businessResults = response.result.map((result: any) => ({
            id: result.id,
            name: result.name,
            address: result.address,
            groupId: result.groupId,
          }));
          setClearBusinessSearchResult(businessResults);
          setSelectedTab(1);
          setRequestCounter((prevCounter) => prevCounter - 1);
          setSubmitting(false);
          setShowFooter(true);
        })
        .catch((error) => {
          setSubmitting(false);
        });
    }
  };

  const businessOptions = [
    { label: merchantBusinessInfo?.merchantName, value: merchantBusinessInfo },
    ...affiliatedBusinesses.map((business) => ({
      label: business?.businessLegalName || "Unknown Business",
      value: business,
    })),
  ];

  return (
    <Box sx={{ p: 2 }}>
      {(generating || generatingHTML || submitting) && <FullPageLoader />}
      <Box
        sx={{
          borderRadius: "10px",
          border: "1px solid rgba(0, 0, 0, 0.1)",
          opacity: 0.8,
          padding: "3px",
          paddingTop: "10px",
          paddingBottom: "0px",
          marginBottom: "5px",
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          marginLeft="12px"
          mb={2}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="Background Check Tabs"
            sx={{ "& .MuiTab-root": { minWidth: "300px", padding: "10px 40px" } }}
            TabIndicatorProps={{
              style: {
                backgroundColor: "#1E8E9B",
              },
            }}
          >
            <Tab
              value="clear"
              icon={
                <img
                  src={ClearIcon}
                  alt="CLEAR"
                  style={{
                    width: 24,
                    height: 24,
                    marginRight: "8px",
                    filter: activeTab === "clear" ? "brightness(0) invert(1)" : "none",
                    objectFit: "contain",
                  }}
                />
              }
              label={
                <span
                  style={{
                    color: activeTab === "clear" ? "#fff" : "#344767",
                    fontWeight: 500,
                  }}
                >
                  CLEAR
                </span>
              }
              sx={{
                backgroundColor: activeTab === "clear" ? "#2F929F" : "inherit",
                borderRadius: "20px",
                marginRight: "8px",
                "&.Mui-selected": {
                  color: "#fff",
                },
                "& .MuiTab-iconWrapper": {
                  color: activeTab === "clear" ? "#fff" : "inherit",
                },
              }}
            />

            <Tab
              value="experian"
              icon={
                <img
                  src={ExperianIcon}
                  alt="EXPERIAN"
                  style={{
                    width: "auto",
                    height: 24,
                    marginRight: "0px",
                    filter:
                      activeTab === "experian" ? "brightness(0) invert(1)" : "brightness(0.25)",
                    objectFit: "contain",
                  }}
                />
              }
              label={
                <span
                  style={{
                    color: activeTab === "experian" ? "#fff" : "#344767",
                    fontWeight: 500,
                  }}
                >
                  EXPERIAN
                </span>
              }
              sx={{
                backgroundColor: activeTab === "experian" ? "#2F929F" : "inherit",
                borderRadius: "20px",
                marginRight: "8px",
                "&.Mui-selected": {
                  color: "#fff",
                },
                "& .MuiTab-iconWrapper": {
                  color: activeTab === "experian" ? "#fff" : "inherit",
                },
              }}
            />
          </Tabs>
        </Box>
      </Box>
      <Box
        sx={{
          borderRadius: "10px",
          border: "1px solid rgba(0, 0, 0, 0.1)",
          opacity: 0.8,
          padding: "4px",
        }}
      >
        <Box display="flex" alignItems="center" mb={2}>
          {(activeTab === "clear" && clearReportList?.length > 0) ||
          (activeTab === "experian" && reports?.length > 0) ? (
            <Typography
              variant="h6"
              gutterBottom
              sx={{ fontSize: "23px", fontWeight: "bold", marginTop: "20px", marginLeft: "15px" }}
            >
              Reports
            </Typography>
          ) : null}
          <Box sx={{ marginLeft: "auto", display: "flex", alignItems: "center" }}>
            <MDButton
              disabled={isReadOnly}
              variant="gradient"
              color="primary"
              onClick={openDialog}
              sx={{ marginTop: "20px", marginRight: "15px" }}
            >
              + Pull New Report
            </MDButton>
          </Box>
        </Box>

        <Box>
          {(activeTab === "clear" && clearReportList?.length > 0) ||
          (activeTab === "experian" && reports?.length > 0) ? (
            <TableContainer
              sx={{ width: "65%", marginLeft: "12px", marginBottom: "12px" }}
              component={Paper}
            >
              <Table>
                {/* Table Head */}
                <TableHead sx={{ backgroundColor: "#f5f5f5", display: "table-header-group" }}>
                  <TableRow sx={{ display: "table-row" }}>
                    {["NAME", "STATUS", "TYPE", "CREATED", "ACTIONS"].map((header, index) => (
                      <TableCell
                        key={index}
                        sx={{
                          fontWeight: "bold",
                          fontSize: "13px",
                          textAlign: "left",
                          paddingLeft: "18px",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: "2px" }}>
                          {header}
                          <UnfoldMoreIcon sx={{ cursor: "pointer", opacity: 0.6 }} />
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {activeTab === "clear" &&
                    clearReportList?.length > 0 &&
                    clearReportList.map((report, index) => (
                      <TableRow key={index}>
                        {[report[1], report[2], report[3], report[4]].map((value, i) => (
                          <TableCell
                            key={i}
                            sx={{
                              padding: "12px",
                              fontSize: "14px",
                              textAlign: "left",
                              paddingLeft: "20px",
                            }}
                          >
                            {value}
                          </TableCell>
                        ))}
                        <TableCell>
                          <MDButton
                            variant="gradient"
                            color="primary"
                            sx={{ backgroundColor: "#1E8E9B", boxShadow: 3 }}
                            onClick={() => getClearReport(report)}
                          >
                            View Report
                          </MDButton>
                        </TableCell>
                      </TableRow>
                    ))}

                  {activeTab === "experian" &&
                    reports?.length > 0 &&
                    reports.map((report, index) => (
                      <TableRow key={index}>
                        {[report.Name, report.status, report.type, report.dateCreated].map(
                          (value, i) => (
                            <TableCell
                              key={i}
                              sx={{
                                padding: "12px",
                                fontSize: "14px",
                                textAlign: "left",
                                paddingLeft: "20px",
                              }}
                            >
                              {value}
                            </TableCell>
                          )
                        )}
                        <TableCell>
                          <MDButton
                            variant="gradient"
                            color="primary"
                            sx={{ backgroundColor: "#1E8E9B", boxShadow: 3 }}
                            onClick={() => getExperianReport(report)}
                          >
                            View Report
                          </MDButton>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : null}
        </Box>
      </Box>

      <Dialog open={isDialogOpen} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle>{activeTab === "experian" ? "EXPERIAN REPORT" : "SEARCH REPORT"}</DialogTitle>
        <DialogContent>
          <Tabs
            value={dialogTab}
            onChange={handleDialogTabChange}
            aria-label="Experian Report Tabs"
            sx={{ "& .MuiTab-root": { minWidth: "auto", width: "140px", padding: "6px 12px" } }}
            TabIndicatorProps={{
              style: {
                backgroundColor: "#1E8E9B",
              },
            }}
          >
            <Tab
              value="personal"
              label={
                activeTab === "experian" ? "Personal Experian Search" : "Personal Clear Search"
              }
              sx={{
                backgroundColor: dialogTab === "personal" ? "#1E8E9B" : "inherit",
                color: dialogTab === "personal" ? "#fff" : "inherit",
                borderRadius: "4px",
                marginRight: "8px",
              }}
            />
            <Tab
              value="business"
              label={
                activeTab === "experian" ? "Business Experian Search" : "Business Clear Search"
              }
              sx={{
                backgroundColor: dialogTab === "business" ? "#1E8E9B" : "inherit",
                color: dialogTab === "business" ? "#fff" : "inherit",
                borderRadius: "4px",
                marginRight: "8px",
              }}
            />
          </Tabs>
          {dialogTab === "personal" && (
            <Box mt={2} display="flex" alignItems="center">
              <FormControl fullWidth sx={{ mt: 1, py: 1, height: "60px", position: "relative" }}>
                {!open && (
                  <InputLabel sx={{ top: "6px" }}>
                    <em>{activeTab === "experian" ? "Select Owner" : "Select Personal Owner"}</em>
                  </InputLabel>
                )}

                <Box sx={{ display: "flex", alignItems: "center", position: "relative" }}>
                  <Select
                    value={
                      activeTab === "experian" ? selectedExperianContact : selectedClearContact
                    }
                    onChange={handleSelectChange}
                    displayEmpty
                    inputProps={{ "aria-label": "Select Owner" }}
                    sx={{ height: "42px", flex: 1 }}
                    onOpen={() => setOpen(true)}
                    onClose={() => setOpen(false)}
                  >
                    <MenuItem value="">
                      <em>{activeTab === "experian" ? "Select Owner" : "Select Personal Owner"}</em>
                    </MenuItem>
                    {contacts.map((contact) => (
                      <MenuItem key={contact.id} value={contact}>
                        {`${contact.firstName} ${contact.lastName} (${contact.email})`}
                      </MenuItem>
                    ))}
                  </Select>

                  <IconButton
                    sx={{
                      position: "absolute",
                      right: 10,
                      top: "50%",
                      transform: "translateY(-50%)",
                    }}
                    onClick={() => setOpen(true)}
                  >
                    <ArrowDropDownIcon />
                  </IconButton>
                </Box>
              </FormControl>
              <Box sx={{ ml: 2, display: "flex" }}>
                <Button onClick={closeDialog} color="primary" sx={{ mr: 2 }}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSearchPersonal}
                  color="primary"
                  disabled={
                    activeTab === "experian" ? !selectedExperianContact : !selectedClearContact
                  }
                >
                  Search
                </Button>
              </Box>
            </Box>
          )}
          {dialogTab === "business" && (
            <Box mt={2} display="flex" alignItems="center">
              <FormControl fullWidth sx={{ mt: 1, py: 1, height: "60px", position: "relative" }}>
                {!open && (
                  <InputLabel sx={{ top: "6px" }}>
                    <em>{activeTab === "experian" ? "Select Business" : "Select Business"}</em>
                  </InputLabel>
                )}

                <Box sx={{ display: "flex", alignItems: "center", position: "relative" }}>
                  <Select
                    value={
                      activeTab === "experian" ? selectedExperianContact : selectedClearContact
                    }
                    onChange={handleSelectChange}
                    displayEmpty
                    inputProps={{ "aria-label": "Select Business" }}
                    sx={{ height: "42px", flex: 1 }} // Allows select to take full width
                    onOpen={() => setOpen(true)}
                    onClose={() => setOpen(false)}
                  >
                    <MenuItem value="">
                      <em>{activeTab === "experian" ? "Select Business" : "Select Business"}</em>
                    </MenuItem>
                    {businessOptions.map((business, index) => (
                      <MenuItem key={index} value={business.value}>
                        {business.label}
                      </MenuItem>
                    ))}
                  </Select>

                  <IconButton
                    sx={{
                      position: "absolute",
                      right: 10,
                      top: "50%",
                      transform: "translateY(-50%)",
                    }}
                    onClick={() => setOpen(!open)}
                  >
                    <ArrowDropDownIcon />
                  </IconButton>
                </Box>
              </FormControl>
              <Box sx={{ ml: 2, display: "flex" }}>
                <Button onClick={closeDialog} color="primary" sx={{ mr: 2 }}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSearchBusiness}
                  color="primary"
                  disabled={
                    activeTab === "experian" ? !selectedExperianContact : !selectedClearContact
                  }
                >
                  Search
                </Button>
              </Box>
            </Box>
          )}
          {showFooter && (
            <Box mt={2}>
              <Typography variant="h6" gutterBottom>
                Confirm Details
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  padding: "8px 10px",
                  fontSize: "50px",
                  borderRadius: "4px",
                  color: "darkslategrey",
                }}
              >
                <Typography sx={{ paddingLeft: 3, fontSize: "18px" }}>Name</Typography>
                <Typography sx={{ paddingLeft: 35, fontSize: "18px" }}>Address</Typography>
              </Box>
              <TableContainer component={Paper}>
                <Table>
                  <TableBody>
                    {dialogTab === "personal" && (
                      <>
                        {/* Render Experian Personal Results */}
                        {activeTab === "experian" &&
                          experianConsumerSearchResult.map(
                            (result) => (
                              console.log(
                                "experianConsumerSearchResult for experian",
                                experianConsumerSearchResult
                              ),
                              (
                                <TableRow key={result.id}>
                                  <TableCell
                                    sx={{ color: "darkgrey", fontSize: "14px", fontWeight: "bold" }}
                                  >
                                    <Checkbox
                                      checked={selectedBin === result.id}
                                      onChange={() => handleCheckboxChange(result.id)}
                                    />
                                    {result.name}
                                  </TableCell>
                                  <TableCell
                                    sx={{ color: "darkgrey", fontSize: "14px", fontWeight: "bold" }}
                                  >
                                    {result.address}
                                  </TableCell>
                                </TableRow>
                              )
                            )
                          )}

                        {/* Render CLEAR Personal Results */}
                        {activeTab === "clear" &&
                          clearConsumerSearchResult.map(
                            (result) => (
                              console.log(
                                "clearConsumerSearchResult for clear",
                                clearConsumerSearchResult
                              ),
                              (
                                <TableRow key={result.id}>
                                  <TableCell
                                    sx={{ color: "darkgrey", fontSize: "14px", fontWeight: "bold" }}
                                  >
                                    <Checkbox
                                      checked={selectedBin === result.groupId}
                                      onChange={() => handleCheckboxChange(result.groupId)}
                                    />
                                    {result.name}
                                  </TableCell>
                                  <TableCell
                                    sx={{ color: "darkgrey", fontSize: "14px", fontWeight: "bold" }}
                                  >
                                    {result.address}
                                  </TableCell>
                                </TableRow>
                              )
                            )
                          )}
                      </>
                    )}

                    {dialogTab === "business" && (
                      <>
                        {/* Render Experian Business Results */}
                        {activeTab === "experian" &&
                          experianBusinessSearchResult.map(
                            (result) => (
                              console.log(
                                "experianBusinessSearchResult for business",
                                experianBusinessSearchResult
                              ),
                              (
                                <TableRow key={result.id}>
                                  <TableCell
                                    sx={{ color: "darkgrey", fontSize: "14px", fontWeight: "bold" }}
                                  >
                                    <Checkbox
                                      checked={selectedBin === result.bin}
                                      onChange={() => handleCheckboxChange(result.bin)}
                                    />
                                    {result.name}
                                  </TableCell>
                                  <TableCell
                                    sx={{ color: "darkgrey", fontSize: "14px", fontWeight: "bold" }}
                                  >
                                    {result.address}
                                  </TableCell>
                                </TableRow>
                              )
                            )
                          )}

                        {/* Render CLEAR Business Results */}
                        {activeTab === "clear" &&
                          clearBusinessSearchResult.map(
                            (result) => (
                              console.log(
                                "clearBusinessSearchResult for business",
                                clearBusinessSearchResult
                              ),
                              (
                                <TableRow key={result.id}>
                                  <TableCell
                                    sx={{ color: "darkgrey", fontSize: "14px", fontWeight: "bold" }}
                                  >
                                    <Checkbox
                                      checked={selectedBin === result.groupId}
                                      onChange={() => handleCheckboxChange(result.groupId)}
                                    />
                                    {result.name}
                                  </TableCell>
                                  <TableCell
                                    sx={{ color: "darkgrey", fontSize: "14px", fontWeight: "bold" }}
                                  >
                                    {result.address}
                                  </TableCell>
                                </TableRow>
                              )
                            )
                          )}
                      </>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {showFooter && (
            <Box mt={2} display="flex" justifyContent="flex-end" width="100%">
              <Button onClick={closeDialog} color="primary" sx={{ mr: 2 }}>
                Cancel
              </Button>
              <MDButton
                onClick={handleGenerateReport}
                color="primary"
                variant="gradient"
                disabled={!selectedBin}
              >
                Generate Report
              </MDButton>
            </Box>
          )}
        </DialogActions>
      </Dialog>
      <SuccessPopup open={popupOpen} message={popupMessage} onClose={() => setPopupOpen(false)} />
    </Box>
  );
};

export default BackgroundChecks;
